import { DialogInfo, TableRowOption } from '@common/types/common';
import { PaginationReturn } from '@common/types/service';
import PlusCompany from '@company/assets/company-plus.svg?react';
import { UserHeader } from '@company/components/display/headers/UserCompanyHeader';
import { CompanyManagementContext } from '@company/contexts/CompanyManagementContext';
import {
    useDeleteManagementUsersFromCompanyMutation,
    useDeleteUserFromCompanyMutation,
    useDeleteUsersFromCompanyMutation,
    useGetCompanyUserRoleQuery,
    useGetCompanyUserRolesQuery,
    useGetCompanyUsersQuery,
    useGetManagementCompanyUsersQuery,
    useUpdateUserRoleMutation,
} from '@company/services/company';
import { CompanyMembersHeader, CompanyTypeColumns, IEmployee, UserRoles } from '@company/types/company';
import { COMPANY_OVERVIEW_TABLE_HEADERS_Public_USER } from '@company/utils/company';
import { CancelDeleteDialog } from '@components/display/dialogs/CancelDeleteDialog';
import { LoadingContainer } from '@components/display/loading/LoadingContainer';
import { Table } from '@components/display/tables';
import { Filters } from '@components/utility/filters';
import { FeatureFlagsContext } from '@contexts/FeatureFlagsContext';
import { useAppSelector } from '@hooks/generic';
import { useDebounce } from '@hooks/useDebounce';
import { usePrefixedNavigate } from '@hooks/usePrefixedNavigate';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Button from '@mui/material/Button/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TableCell from '@mui/material/TableCell/TableCell';
import Typography from '@mui/material/Typography';
import { selectUser } from '@store/userSlice';
import { handleError, toaster } from '@utils/common';
import clsx from 'clsx';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const allowedColumns: CompanyTypeColumns[] = ['id', 'user__full_name', 'role'];

const UserManagement = () => {
    const { enableRoles } = useContext(FeatureFlagsContext);
    const { permissions, company } = useContext(CompanyManagementContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const isDashboard = useMemo(() => window.location.pathname.includes('/dashboard/'), []);
    const user = useAppSelector(selectUser);
    const navigate = usePrefixedNavigate();

    const filters = useMemo(
        () => ({
            sort: allowedColumns.includes(searchParams.get('sort')?.replace('-', '') as CompanyTypeColumns)
                ? (searchParams.get('sort') as CompanyTypeColumns)
                : 'user__full_name',
            page: Number.isNaN(searchParams.get('page')) || !searchParams.get('page') ? '1' : searchParams.get('page'),
            search: permissions?.filter ? searchParams.get('search') : '',
            role: permissions?.filter && enableRoles ? searchParams.get('role') : null,
        }),
        [enableRoles, permissions?.filter, searchParams]
    );
    const sort: { column: CompanyTypeColumns; direction: 'asc' | 'desc' } = useMemo(
        () => ({
            column: filters.sort.replace('-', '') as CompanyTypeColumns,
            direction: filters.sort.includes('-') ? 'desc' : 'asc',
        }),
        [filters.sort]
    );

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [search, setSearch] = useState(filters.search);
    const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [dialogInfo, setDialogInfo] = useState<DialogInfo | null>(null);
    const [anchorElRole, setAnchorElRole] = useState<null | HTMLElement>(null);
    const selectedActionsRowIdRef = useRef<string | null>(null);

    const { data: userRole, isFetching: isUserRoleFetching } = useGetCompanyUserRoleQuery(
        { company_id: company?.id ?? '' },
        { skip: isDashboard }
    );
    const {
        data: companyUsersResponse,
        isFetching: isFetchingCompanyUsers,
        error: companyUsersError,
    } = useGetCompanyUsersQuery(
        {
            company_id: company?.id ?? '',
            ordering: filters.sort,
            page: filters.page ? Number(filters.page) : 1,
            page_size: 20,
            filters: {
                role_id: enableRoles && filters.role ? [filters.role] : [],
                search_user_name: filters.search ? [filters.search] : [],
            },
        },
        { skip: isDashboard }
    );
    const companyUsers = companyUsersResponse as PaginationReturn<IEmployee> | undefined;

    const {
        data: managementCompanyUsersResponse,
        isFetching: isFetchingManagementCompanyUsers,
        error: managementCompanyUsersError,
    } = useGetManagementCompanyUsersQuery(
        {
            company_id: company?.id ?? '',
            ordering: filters.sort,
            page: filters.page ? Number(filters.page) : 1,
            page_size: 20,
            filters: {
                role_id: enableRoles && filters.role ? [filters.role] : [],
                search_user_name: filters.search ? [filters.search] : [],
            },
        },
        { skip: !isDashboard }
    );
    const { data: userRoles, isLoading: isRolesLoading } = useGetCompanyUserRolesQuery(undefined, {
        skip: !enableRoles,
    });
    const managementCompanyUsers = managementCompanyUsersResponse as PaginationReturn<IEmployee> | undefined;
    const users = useMemo(() => companyUsers || managementCompanyUsers, [companyUsers, managementCompanyUsers]);
    const error = useMemo(
        () => companyUsersError || managementCompanyUsersError,
        [companyUsersError, managementCompanyUsersError]
    );

    const [leaveCompany, { isLoading: isDeleteCompanyForUserLoading }] = useDeleteUserFromCompanyMutation();
    const [deleteUsersFromCompany, { isLoading: isDeleteUsersLoading }] = useDeleteUsersFromCompanyMutation();
    const [deleteManagementUsersFromCompany, { isLoading: isDeleteManagementUsersLoading }] =
        useDeleteManagementUsersFromCompanyMutation();
    const [updateUserRole] = useUpdateUserRoleMutation();

    const resetFilters = useCallback(() => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.set('page', '1');
            params.delete('role');
            return params;
        });
    }, [setSearchParams]);

    const handleParamChange = useCallback(
        (field: string, value: string | null) => {
            if (searchParams.get(field) !== value) {
                setSearchParams((prev) => {
                    const params = new URLSearchParams(prev);
                    params.set('page', '1');
                    if (value) {
                        params.set(field, value);
                    } else {
                        params.delete(field);
                    }
                    return params;
                });
            }
        },
        [searchParams, setSearchParams]
    );

    const handleChipLabel = useCallback((field: string, value: string) => {
        switch (field) {
            case 'role':
                return Object.values(UserRoles)[Number(value) - 1];
            default:
                return '';
        }
    }, []);

    const setDialog = useCallback((title: string, primaryLabel: string, primaryOnClick: () => void) => {
        setDialogInfo({
            title,
            primary: {
                label: primaryLabel,
                onClick: primaryOnClick,
            },
            secondary: {
                label: 'Go back',
                onClick: () => setDialogInfo(null),
            },
        });
    }, []);

    const handleRemoveUsers = useCallback(
        async (usersSelected: string[], successMessage: string) => {
            try {
                if (permissions?.remove && company) {
                    if (isDashboard) {
                        await deleteManagementUsersFromCompany({
                            company_id: company.id,
                            usersSelected,
                        }).unwrap();
                    } else {
                        await deleteUsersFromCompany({
                            company_id: company.id,
                            usersSelected,
                        }).unwrap();
                    }
                    toaster.success({ msg: successMessage });
                    setDialogInfo(null);
                    setSelectedItems(
                        usersSelected.length === 1 ? selectedItems.filter((x) => x !== usersSelected[0]) : []
                    );
                }
            } catch (err) {
                handleError(`Error removing ${usersSelected.length > 1 ? 'users' : 'user'}`, err);
            }
        },
        [
            permissions?.remove,
            company,
            isDashboard,
            selectedItems,
            deleteManagementUsersFromCompany,
            deleteUsersFromCompany,
        ]
    );

    const handleLeaveCompany = useCallback(async () => {
        try {
            if (user && company) {
                await leaveCompany({ company_id: company.id, user_id: userRole?.id.toString() ?? '' }).unwrap();
                toaster.success({ msg: 'Successfully left the company' });
                setSelectedItems([]);
                setDialogInfo(null);
                navigate('/companies/');
            }
        } catch (err) {
            handleError('Error leaving company', err);
        }
    }, [company, leaveCompany, navigate, user, userRole?.id]);

    const rightFilterComponent = useMemo(
        () => (
            <div className="xs:ml-auto flex gap-2 flex-col 2md:flex-row">
                <Button
                    variant="outlined"
                    color="inherit"
                    onClick={(e) => setActionsAnchorEl(e.currentTarget)}
                    endIcon={<KeyboardArrowDownIcon />}
                    className="!bg-anti-flash-white !border-none"
                    disabled={selectedItems.length === 0}
                >
                    Actions
                </Button>
                <Menu
                    anchorEl={actionsAnchorEl}
                    open={Boolean(actionsAnchorEl)}
                    onClose={() => setActionsAnchorEl(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    <MenuItem
                        onClick={() => {
                            setDialog('Are you sure you want to remove these employees?', 'Remove', () =>
                                handleRemoveUsers(selectedItems, 'Users removed')
                            );
                            setActionsAnchorEl(null);
                        }}
                        sx={{ minWidth: 150, justifyContent: 'center' }}
                        disabled={selectedItems.length === 0}
                    >
                        <Typography color={'error'}>Remove ({selectedItems.length})</Typography>
                    </MenuItem>
                </Menu>
                <Button
                    variant="contained"
                    color="primary"
                    sx={{ minWidth: 120, maxHeight: 40 }}
                    startIcon={<PlusCompany className="h-6 w-6" />}
                    className="text-nowrap !hidden"
                >
                    Invite User
                </Button>
            </div>
        ),
        [actionsAnchorEl, handleRemoveUsers, selectedItems, setDialog]
    );

    const renderRow = useCallback(
        (employee: Partial<IEmployee>) => (
            <>
                <TableCell className="ml-10 w-72">{employee.user?.full_name}</TableCell>
                <TableCell>
                    {enableRoles &&
                    permissions?.edit &&
                    employee.id &&
                    employee?.user?.id.toString() !== user?.id.toString() ? (
                        <>
                            <Button
                                variant="outlined"
                                color="inherit"
                                endIcon={
                                    <KeyboardArrowDownIcon
                                        sx={{ marginLeft: employee.role?.name === UserRoles.ADMIN ? '30px' : '8px' }}
                                    />
                                }
                                sx={{
                                    width: 128,
                                    maxHeight: 40,
                                    borderRadius: 3,
                                    backgroundColor: 'white',
                                    borderColor: 'gray',
                                    font: 'unset',
                                }}
                                onClick={(event) => {
                                    selectedActionsRowIdRef.current = employee.id as string;
                                    setAnchorElRole(event.currentTarget);
                                }}
                            >
                                {employee?.role?.name}
                            </Button>
                        </>
                    ) : (
                        <span
                            className={clsx('flex py-1.5 px-7 border-2 rounded-xl w-32 justify-center', {
                                'bg-black text-white border-none': employee.is_owner,
                                'bg-white text-black': employee.role?.name === UserRoles.EMPLOYEE,
                            })}
                        >
                            {employee.is_owner ? 'Owner' : employee.role?.name}
                        </span>
                    )}
                </TableCell>
            </>
        ),
        [enableRoles, permissions?.edit, user?.id]
    );

    const handleSortClick = useCallback(
        (column: keyof CompanyMembersHeader) => {
            const isAsc = sort.column === column && sort.direction === 'asc';
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.set('sort', `${isAsc ? '-' : ''}${column}`);
                return params;
            });
        },
        [setSearchParams, sort.column, sort.direction]
    );

    const handleSelected = useCallback((row: IEmployee) => {
        const row_ = row.id;
        setSelectedItems((prevSelectedItems) => {
            const exists = prevSelectedItems.indexOf(row_) !== -1;
            return exists ? prevSelectedItems.filter((x) => x !== row_) : [...prevSelectedItems, row_];
        });
    }, []);

    const handleSelectAll = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.checked) {
                setSelectedItems([...selectedItems, ...(users?.queryset_ids.map(String) ?? [])]);
            } else {
                setSelectedItems(selectedItems.filter((x) => !users?.queryset_ids.includes(x)));
            }
        },
        [users?.queryset_ids, selectedItems]
    );

    const handlePageChange = useCallback(
        (newPage: number) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.set('page', `${newPage}`);
                return params;
            });
        },
        [setSearchParams]
    );

    const rowOptions: TableRowOption<IEmployee>[] | undefined = useMemo(() => {
        return permissions?.remove
            ? [
                  {
                      label: 'Remove',
                      color: 'error',
                      onClick: (event) => {
                          selectedActionsRowIdRef.current = event?.id as string;
                          setDialog('Are you sure you want to remove this employee?', 'Remove', () =>
                              handleRemoveUsers([selectedActionsRowIdRef.current as string], 'User removed')
                          );
                      },
                  },
              ]
            : undefined;
    }, [handleRemoveUsers, permissions?.remove, setDialog]);

    const handleSetUserRoleCompany = useCallback(
        async (roleId: number) => {
            try {
                if (company?.id && selectedActionsRowIdRef.current) {
                    await updateUserRole({
                        company_id: company.id,
                        user_id: selectedActionsRowIdRef.current as string,
                        role_id: roleId as number,
                    }).unwrap();
                    toaster.success({ msg: 'Event successfully removed' });
                }
            } catch (err) {
                handleError('Error leaving company', err);
            }
        },
        [company?.id, updateUserRole]
    );

    const handleSearchParamChange = useCallback(
        (value: string | null) => {
            handleParamChange('search', value);
        },
        [handleParamChange]
    );

    useDebounce({
        value: search,
        delay: 500,
        customHandler: handleSearchParamChange,
        trigger: () => !!permissions?.filter,
    });
    useEffect(() => {
        if (permissions?.filter) {
            setSearch(filters.search);
        }
    }, [filters.search, permissions?.filter]);

    return !error ? (
        <LoadingContainer isLoading={isRolesLoading || isUserRoleFetching}>
            {permissions?.filter ? (
                <Filters
                    searchInput={search ?? ''}
                    onSearchChange={(e) => setSearch(e.target.value)}
                    onSearchClear={() => setSearch('')}
                    searchPlaceholder="Search for user..."
                    isOpen={isFilterOpen}
                    onFilterOpenToggle={() => setIsFilterOpen(!isFilterOpen)}
                    onClearFilterClick={resetFilters}
                    activeFilters={{}}
                    onChipClick={(field) => handleParamChange(field, '')}
                    getChipLabel={handleChipLabel}
                    right={permissions.remove ? rightFilterComponent : null}
                />
            ) : (
                <UserHeader
                    handleSetDialogLeaveCompanyAsUser={() =>
                        setDialog('Are you sure you want to leave this company?', 'Leave', handleLeaveCompany)
                    }
                />
            )}
            <Table
                headers={COMPANY_OVERVIEW_TABLE_HEADERS_Public_USER}
                data={users?.results ?? []}
                renderRow={renderRow}
                selectable={permissions?.remove}
                isRowSelectable={(row: Partial<IEmployee>) => !!permissions?.remove && row.user?.id !== user?.id}
                emptyListTxt="There are currently no employees to list"
                rowsPerPage={20}
                selectedItems={selectedItems}
                onSortClick={handleSortClick}
                onRowCheckClick={handleSelected}
                onSelectAll={handleSelectAll}
                page={Number(filters.page)}
                onPageChange={handlePageChange}
                sort={sort}
                count={users?.count || 0}
                rowOptions={{ data: rowOptions }}
                showRowOptions={(row: Partial<IEmployee>) => row.user?.id !== user?.id}
                isLoading={isFetchingCompanyUsers || isFetchingManagementCompanyUsers}
                containerClassname="px-5 pb-5"
                fillMissingRows
            />
            {enableRoles && (
                <Menu
                    anchorEl={anchorElRole}
                    open={Boolean(anchorElRole)}
                    onClose={() => setAnchorElRole(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    {userRoles?.map((role, index) => (
                        <MenuItem sx={{ minWidth: 150, justifyContent: 'center' }} key={index}>
                            <Typography
                                onClick={() => {
                                    handleSetUserRoleCompany(role.id);
                                    setAnchorElRole(null);
                                }}
                            >
                                {role.name}
                            </Typography>
                        </MenuItem>
                    ))}
                </Menu>
            )}
            <CancelDeleteDialog
                open={!!dialogInfo}
                title={dialogInfo?.title}
                primary={{
                    label: dialogInfo?.primary.label || '',
                    onClick: dialogInfo?.primary.onClick || (() => {}),
                    loading: isDeleteUsersLoading || isDeleteManagementUsersLoading || isDeleteCompanyForUserLoading,
                }}
                secondary={{
                    label: dialogInfo?.secondary.label || '',
                    onClick: () => setDialogInfo(null),
                }}
            />
        </LoadingContainer>
    ) : (
        <p>An error occured while fetching users</p>
    );
};

export { UserManagement };
